import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import ApiIcon from "@mui/icons-material/Api";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import LayersIcon from "@mui/icons-material/Layers";
import HubIcon from "@mui/icons-material/Hub";
import VerifiedIcon from "@mui/icons-material/Verified";

export default function ApiMicroservices() {
  return (
    <Box sx={{ backgroundColor: "#020617", color: "#e5e7eb" }}>
      {/* ================= HERO ================= */}
      <Box sx={{ py: 12 }}>
        <Container>
          <Typography
            variant="h3"
            align="center"
            fontWeight={600}
            gutterBottom
          >
            APIs & Microservices
          </Typography>

          <Typography
            align="center"
            sx={{ maxWidth: 900, mx: "auto", opacity: 0.9 }}
          >
            We design and build scalable, secure, and high-performance APIs and
            microservices that serve as the backbone of modern digital platforms
            and enterprise ecosystems.
          </Typography>

          {/* Tabs */}
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            mt={6}
            flexWrap="wrap"
          >
            <Button sx={tab}>API-First Architecture</Button>
            <Button sx={tab}>Microservices Design</Button>
            <Button sx={tab}>System Integration</Button>
            <Button sx={tab}>Cloud-Native</Button>
          </Stack>

          <Divider sx={{ mt: 3, borderColor: "#1e293b" }} />
        </Container>
      </Box>

      {/* ================= CORE SECTION ================= */}
      <Container sx={{ py: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom>
              API-First & Microservices-Driven Development
            </Typography>

            <Typography sx={{ mb: 3, opacity: 0.9 }}>
              Our approach focuses on building modular, loosely coupled services
              that communicate through well-defined APIs. This enables faster
              development, independent scaling, and seamless integrations.
            </Typography>

            <Typography sx={{ opacity: 0.85 }}>
              From internal enterprise systems to public-facing platforms, our
              APIs ensure reliability, flexibility, and long-term maintainability.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1555949963-aa79dcee981c?auto=format&fit=crop&w=1200&q=80"
              alt="API Architecture"
              sx={{
                width: "100%",
                borderRadius: 3,
                boxShadow: "0 20px 40px rgba(0,0,0,0.45)",
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* ================= WHY CHOOSE US ================= */}
      <Box sx={{ backgroundColor: "#020617", py: 12 }}>
        <Container>
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            fontWeight={600}
          >
            Why Choose NerdsHub for APIs & Microservices?
          </Typography>

          <Grid container spacing={8} mt={4}>
            {/* Left Image */}
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31"
                alt="Microservices"
                sx={{ width: "100%", borderRadius: 3 }}
              />
            </Grid>

            {/* Right Content */}
            <Grid item xs={12} md={6}>
              <Feature
                icon={<LayersIcon />}
                title="Scalable Architecture"
                desc="Designed to handle growth effortlessly through horizontal scaling, containerization, and service isolation."
              />

              <Feature
                icon={<SpeedIcon />}
                title="High Performance"
                desc="Optimized for low latency and high throughput using caching, asynchronous processing, and efficient communication protocols."
              />

              <Feature
                icon={<HubIcon />}
                title="Seamless Integrations"
                desc="APIs built for easy integration with web, mobile, third-party services, and legacy systems."
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ================= SECURITY & CLOUD ================= */}
      <Container sx={{ py: 12 }}>
        <Grid container spacing={8}>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1563986768609-322da13575f3"
              alt="API Security"
              sx={{ width: "100%", borderRadius: 3 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Feature
              icon={<SecurityIcon />}
              title="Enterprise-Grade Security"
              desc="Robust authentication, authorization, rate-limiting, encryption, and compliance with industry standards."
            />

            <Feature
              icon={<ApiIcon />}
              title="API Governance & Monitoring"
              desc="Versioning, documentation, logging, and monitoring to ensure reliability and long-term sustainability."
            />

            <Feature
              icon={<VerifiedIcon />}
              title="Cloud-Native Ready"
              desc="Built for AWS, Azure, and GCP with containerized deployments, CI/CD pipelines, and observability."
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

/* ================= REUSABLE FEATURE ================= */

function Feature({ icon, title, desc }) {
  return (
    <Box sx={{ display: "flex", mb: 5 }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          backgroundColor: "#ec4899",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mr: 3,
          color: "#fff",
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography sx={{ opacity: 0.85 }}>{desc}</Typography>
      </Box>
    </Box>
  );
}

/* ================= STYLES ================= */

const tab = {
  color: "#9ca3af",
  textTransform: "none",
};
